/* @flow */

import clsx from 'clsx';
import { produce } from 'immer';
import * as React from 'react';
import { connect } from 'react-redux';

import type { BasicFunction } from '../../helpers/jsHelpers/types';
import type { Settings, SettingValue } from '../../helpers/settings/types';
import type { CombinedReducers } from '../../redux/reducers';
import type { Dispatch } from '../../redux/types/types';
import type { CONFIRMATION_DATA_MODAL_TYPE } from '../modal/confirmationModal/ConfirmationModal';

import GameManager from '../../helpers/gaming/gameManager';
import Localizer from '../../helpers/localization';
import { Messenger, MessengerEvents } from '../../helpers/messenger';
import { getBooleanSetting, getNumberSetting, getStringSetting } from '../../helpers/settings/settings';
import { Setting } from '../../helpers/settings/types';
import { convertToHumanReadable } from '../../helpers/ui/language';
import { showConfirmationModal } from '../../redux/modal/actions';
import { deleteWholePlayingHistory } from '../../redux/netgemApi/actions/personalData/playingHistory';
import { deleteWholeViewingHistory } from '../../redux/netgemApi/actions/personalData/viewingHistory';
import { clearWishlist } from '../../redux/netgemApi/actions/personalData/wishlist';
import { deleteFavoriteList } from '../../redux/netgemApi/actions/videofutur/helpers/favorite';
import { deleteSearchHistory, resetSettings, updateSetting } from '../../redux/ui/actions';
import { ConfirmationModalResult } from '../modal/confirmationModal/ConfirmationModal';
import { PictoHearingImpaired, PictoLeaf, PictoVolume, PictoVolumeUp } from '../pictos/Element';
import Slider from '../slider/Slider';
import IntersectionWaypoint from '../waypoint/IntersectionWaypoint';
import { DefaultTimeSliderValues, type SliderSetting } from './AppSettingsConstsAndTypes';
import { convertSliderValueToVolume, convertVolumeToSliderValue, renderTimeSliderLabel, renderTimeSliderValue, renderVolumeSliderLabel, renderVolumeSliderValue } from './helpers';
import RowSettingAction from './RowSettingAction';
import './SharedSettings.css';
import RowSettingToggle from './RowSettingToggle';

type ReduxSharedSettingsReducerStateType = {
  +isMaxBitrateAllowed: boolean,
  +settings: Settings,
};

type ReduxSharedSettingsDispatchToPropsType = {
  +localClearWishlist: () => Promise<void>,
  +localDeleteFavoriteList: () => Promise<void>,
  +localDeletePlayingHistory: () => Promise<void>,
  +localDeleteSearchHistory: BasicFunction,
  +localDeleteViewingHistory: () => Promise<void>,
  +localResetSettings: () => Promise<void>,
  +localShowConfirmation: (data: CONFIRMATION_DATA_MODAL_TYPE, ariaLabel: string) => void,
  +localUpdateSetting: (setting?: Setting, value: SettingValue) => void,
};

type SharedSettingsPropType = {
  +audioDataIndex?: number,
  +audioVideoRef?: (instance: HTMLElement | null) => void,
  +contentDataIndex?: number,
  +contentRef?: (instance: HTMLElement | null) => void,
  +onWaypointChange?: (index: number) => (entry: IntersectionObserverEntry) => void,
};

type CompleteSharedSettingsPropType = {
  ...SharedSettingsPropType,
  ...ReduxSharedSettingsReducerStateType,
  ...ReduxSharedSettingsDispatchToPropsType,
};

type SharedSettingsStateType = {
  isExpertMode: boolean,
};

class SharedSettings extends React.PureComponent<CompleteSharedSettingsPropType, SharedSettingsStateType> {
  constructor(props: CompleteSharedSettingsPropType) {
    super(props);

    this.state = { isExpertMode: false };
  }

  handleExpertModeOnClick: () => void = () => {
    this.setState(
      produce((draft) => {
        draft.isExpertMode = !draft.isExpertMode;
      }),
    );
  };

  handleResetOnClick: () => void = () => {
    const { localShowConfirmation } = this.props;

    const data: CONFIRMATION_DATA_MODAL_TYPE = {
      button1Title: Localizer.localize('common.actions.cancel'),
      button2Title: Localizer.localize('settings.actions.reset'),
      header: Localizer.localize('settings.more.reset_confirmation_title'),
      question: Localizer.localize('settings.more.reset_confirmation_question'),
    };

    Messenger.once(MessengerEvents.MODAL_CLOSED, this.resetSettingsConfirmationClosedCallback);
    localShowConfirmation(data, Localizer.localize('accessibility.modal.confirmation_reset_settings'));
  };

  resetSettingsConfirmationClosedCallback: (result: ConfirmationModalResult) => void = (result) => {
    const { localResetSettings } = this.props;

    if (result !== ConfirmationModalResult.Button2) {
      // Reset canceled
      return;
    }

    localResetSettings();
  };

  handleSettingOnChanged: (value: any, setting?: Setting) => void = (value, setting) => {
    const { localUpdateSetting } = this.props;

    if (!setting) {
      return;
    }

    localUpdateSetting(setting, value);

    if (setting === Setting.BufferingGoal) {
      this.checkRebufferingGoal(value as number);
    }
  };

  handleBoolSettingOnChanged: (checked: boolean, setting?: Setting) => void = (checked, setting) => {
    const { localUpdateSetting } = this.props;

    if (!setting) {
      return;
    }

    localUpdateSetting(setting, checked);
  };

  handleDeletePlayingHistoryOnClick: () => void = () => {
    const { localShowConfirmation } = this.props;

    const data: CONFIRMATION_DATA_MODAL_TYPE = {
      button1Title: Localizer.localize('common.actions.cancel'),
      button2Title: Localizer.localize('common.actions.delete_all'),
      header: Localizer.localize('common.titles.delete_confirmation'),
      question: Localizer.localize('settings.content.playing_history.delete_confirmation_question'),
    };

    Messenger.once(MessengerEvents.MODAL_CLOSED, this.playingHistoryClearConfirmationClosedCallback);
    localShowConfirmation(data, Localizer.localize('accessibility.modal.confirmation_delete_playing_history'));
  };

  playingHistoryClearConfirmationClosedCallback = (result: ConfirmationModalResult): void => {
    if (result !== ConfirmationModalResult.Button2) {
      // Clear canceled
      return;
    }

    const { localDeletePlayingHistory } = this.props;

    localDeletePlayingHistory()
      .then(() => Messenger.emit(MessengerEvents.NOTIFY_SUCCESS, Localizer.localize('settings.content.playing_history.delete_success')))
      .catch(() => Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('settings.content.playing_history.delete_error')));
  };

  handleDeleteViewingHistoryOnClick: () => void = () => {
    const { localShowConfirmation } = this.props;

    const data: CONFIRMATION_DATA_MODAL_TYPE = {
      button1Title: Localizer.localize('common.actions.cancel'),
      button2Title: Localizer.localize('common.actions.delete_all'),
      header: Localizer.localize('common.titles.delete_confirmation'),
      question: Localizer.localize('settings.content.viewing_history.delete_confirmation_question'),
    };

    Messenger.once(MessengerEvents.MODAL_CLOSED, this.viewingHistoryClearConfirmationClosedCallback);
    localShowConfirmation(data, Localizer.localize('accessibility.modal.confirmation_delete_viewing_history'));
  };

  viewingHistoryClearConfirmationClosedCallback = (result: ConfirmationModalResult): void => {
    if (result !== ConfirmationModalResult.Button2) {
      // Clear canceled
      return;
    }

    const { localDeleteViewingHistory } = this.props;

    localDeleteViewingHistory()
      .then(() => Messenger.emit(MessengerEvents.NOTIFY_SUCCESS, Localizer.localize('settings.content.viewing_history.delete_success')))
      .catch(() => Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('settings.content.viewing_history.delete_error')));
  };

  handleDeleteSearchHistoryOnClick: () => void = () => {
    const { localShowConfirmation } = this.props;

    const data: CONFIRMATION_DATA_MODAL_TYPE = {
      button1Title: Localizer.localize('common.actions.cancel'),
      button2Title: Localizer.localize('common.actions.delete_all'),
      header: Localizer.localize('common.titles.delete_confirmation'),
      question: Localizer.localize('settings.content.search_history.delete_confirmation_question'),
    };

    Messenger.once(MessengerEvents.MODAL_CLOSED, this.searchHistoryClearConfirmationClosedCallback);
    localShowConfirmation(data, Localizer.localize('accessibility.modal.confirmation_delete_search_history'));
  };

  searchHistoryClearConfirmationClosedCallback = (result: ConfirmationModalResult): void => {
    if (result !== ConfirmationModalResult.Button2) {
      // Clear canceled
      return;
    }

    const { localDeleteSearchHistory } = this.props;
    localDeleteSearchHistory();
    window.requestAnimationFrame(() => Messenger.emit(MessengerEvents.NOTIFY_SUCCESS, Localizer.localize('settings.content.search_history.delete_success')));
  };

  handleClearWishlistOnClick: () => void = () => {
    const { localShowConfirmation } = this.props;

    const data: CONFIRMATION_DATA_MODAL_TYPE = {
      button1Title: Localizer.localize('common.actions.cancel'),
      button2Title: Localizer.localize('common.actions.delete_all'),
      header: Localizer.localize('settings.content.wishlist.delete_confirmation_title'),
      question: Localizer.localize('settings.content.wishlist.delete_confirmation_question'),
    };

    Messenger.once(MessengerEvents.MODAL_CLOSED, this.wishlistClearConfirmationClosedCallback);
    localShowConfirmation(data, Localizer.localize('accessibility.modal.confirmation_delete_wishlist'));
  };

  wishlistClearConfirmationClosedCallback = (result: ConfirmationModalResult): void => {
    if (result !== ConfirmationModalResult.Button2) {
      // Clear canceled
      return;
    }

    const { localClearWishlist, localDeleteFavoriteList } = this.props;
    Promise.all([localDeleteFavoriteList(), localClearWishlist()]).then(() => {
      Messenger.emit(MessengerEvents.NOTIFY_SUCCESS, Localizer.localize('settings.content.wishlist.delete_success'));
    });
  };

  // Rebuffering goal should be strictly lower than buffering goal. This function ensures the slider cannot be dragged too far.
  validateRebufferingGoal = (value: number): boolean => {
    const { settings } = this.props;

    const bufferingGoal = getNumberSetting(settings, Setting.BufferingGoal);

    return value < bufferingGoal;
  };

  // Rebuffering goal should be strictly lower than buffering goal. This function ensures its value is updated if buffering goal has become <= rebuffering goal.
  checkRebufferingGoal = (bufferingGoal: number): void => {
    const { localUpdateSetting, settings } = this.props;

    const rebufferingGoal = getNumberSetting(settings, Setting.RebufferingGoal);

    if (rebufferingGoal >= bufferingGoal) {
      localUpdateSetting(Setting.RebufferingGoal, bufferingGoal - 1);
    }
  };

  renderContentSettings = (): React.Node => {
    const { contentDataIndex, contentRef, onWaypointChange } = this.props;

    return (
      <div className='settingsSection' data-index={contentDataIndex} ref={contentRef}>
        {onWaypointChange && typeof contentDataIndex === 'number' ? <IntersectionWaypoint onChange={onWaypointChange(contentDataIndex)} /> : null}
        <div className='sectionTitle'>{Localizer.localize('settings.content.title')}</div>
        <RowSettingAction className='settingsRow' onClick={this.handleDeleteViewingHistoryOnClick}>
          <div className='settingsLabel'>{Localizer.localize('settings.content.viewing_history.delete')}</div>
        </RowSettingAction>
        {GameManager.isGamingEnabled ? (
          <RowSettingAction className='settingsRow' onClick={this.handleDeletePlayingHistoryOnClick}>
            <div className='settingsLabel'>{Localizer.localize('settings.content.playing_history.delete')}</div>
          </RowSettingAction>
        ) : null}
        <RowSettingAction className='settingsRow' onClick={this.handleDeleteSearchHistoryOnClick}>
          <div className='settingsLabel'>{Localizer.localize('settings.content.search_history.delete')}</div>
        </RowSettingAction>
        <RowSettingAction className='settingsRow' onClick={this.handleClearWishlistOnClick}>
          <div className='settingsLabel'>{Localizer.localize('settings.content.wishlist.delete')}</div>
        </RowSettingAction>
      </div>
    );
  };

  renderSliderSetting = (data: SliderSetting): React.Node => {
    const { id, isExpertMode = false, label, labelRenderer, max, min, picto, setting, step, validate, valueConverter, valueConverterBack, valueRenderer } = data;
    const { settings } = this.props;

    const value = getNumberSetting(settings, setting);

    const labelId = `${id}-label`;
    const settingId = `${id}-setting`;

    return (
      <div className={clsx('settingsRow', isExpertMode && 'expert')}>
        <label className='settingsLabel' htmlFor={settingId} id={labelId}>
          {label}
        </label>
        <div className='value slider'>
          {picto ?? null}
          <Slider
            ariaLabelledby={labelId}
            id={settingId}
            labelRenderer={labelRenderer}
            max={max}
            min={min}
            onChange={this.handleSettingOnChanged}
            setting={setting}
            showValue
            step={step}
            validate={validate}
            value={value}
            valueConverter={valueConverter}
            valueConverterBack={valueConverterBack}
            valueRenderer={valueRenderer}
          />
        </div>
      </div>
    );
  };

  renderVolumeSetting = (): React.Node =>
    this.renderSliderSetting({
      id: 'volume',
      label: Localizer.localize('settings.audio_video.volume.label'),
      labelRenderer: renderVolumeSliderLabel,
      max: 100,
      min: 0,
      picto: (
        <>
          <PictoVolume aria-hidden className='speaker' />
          <PictoVolumeUp aria-hidden className='speaker waves' />
        </>
      ),
      setting: Setting.Volume,
      step: 1,
      valueConverter: convertVolumeToSliderValue,
      valueConverterBack: convertSliderValueToVolume,
      valueRenderer: renderVolumeSliderValue,
    });

  renderAudioTrackSetting = (): React.Node => {
    const { settings } = this.props;

    const autoSelectAudioTrack = getBooleanSetting(settings, Setting.AutoSelectAudioTrack);
    const lastAudioTrack = getStringSetting(settings, Setting.LastAudioTrack);

    return (
      <RowSettingToggle checked={autoSelectAudioTrack} className='settingsRow' onChange={this.handleBoolSettingOnChanged} setting={Setting.AutoSelectAudioTrack}>
        <div className='settingsLabel'>
          <div>{Localizer.localize('settings.audio_video.auto_select_audio_track.label')}</div>
          <div className='value'>{convertToHumanReadable(lastAudioTrack) || Localizer.localize('settings.audio_video.last_audio_track.none')}</div>
        </div>
      </RowSettingToggle>
    );
  };

  renderSubtitlesTrackSetting = (): React.Node => {
    const { settings } = this.props;

    const autoSelectSubtitlesTrack = getBooleanSetting(settings, Setting.AutoSelectSubtitlesTrack);
    const lastSubtitlesTrack = getStringSetting(settings, Setting.LastSubtitlesTrack);

    const [subsLanguage, isHearingImpaired] = lastSubtitlesTrack.split(':');

    return (
      <RowSettingToggle checked={autoSelectSubtitlesTrack} className='settingsRow' onChange={this.handleBoolSettingOnChanged} setting={Setting.AutoSelectSubtitlesTrack}>
        <div className='settingsLabel'>
          <div>{Localizer.localize('settings.audio_video.auto_select_subtitles_track.label')}</div>
          <div className='value'>
            {isHearingImpaired ? <PictoHearingImpaired className='impaired' /> : null}
            {convertToHumanReadable(subsLanguage) || Localizer.localize('settings.audio_video.last_subtitles_track.none')}
          </div>
        </div>
      </RowSettingToggle>
    );
  };

  renderAutoResumeSetting = (): React.Node => {
    const { settings } = this.props;

    const autoResume = getBooleanSetting(settings, Setting.AutoResume);

    return (
      <RowSettingToggle checked={autoResume} className='settingsRow' onChange={this.handleBoolSettingOnChanged} setting={Setting.AutoResume}>
        <div className='settingsLabel column'>
          <div>{Localizer.localize('settings.audio_video.auto_resume.label')}</div>
          <div className='hint'>{Localizer.localize('settings.audio_video.auto_resume.hint')}</div>
        </div>
      </RowSettingToggle>
    );
  };

  renderExpertSettings = (): React.Node => (
    <>
      {this.renderSliderSetting({
        ...DefaultTimeSliderValues,
        id: 'buffering-goal',
        isExpertMode: true,
        label: Localizer.localize('settings.audio_video.buffering_goal.label'),
        labelRenderer: renderTimeSliderLabel,
        min: 2,
        setting: Setting.BufferingGoal,
        valueRenderer: renderTimeSliderValue,
      })}
      {this.renderSliderSetting({
        ...DefaultTimeSliderValues,
        id: 'rebuffering-goal',
        isExpertMode: true,
        label: Localizer.localize('settings.audio_video.rebuffering_goal.label'),
        labelRenderer: renderTimeSliderLabel,
        min: 1,
        setting: Setting.RebufferingGoal,
        validate: this.validateRebufferingGoal,
        valueRenderer: renderTimeSliderValue,
      })}
      {this.renderSliderSetting({
        ...DefaultTimeSliderValues,
        id: 'buffer-behind',
        isExpertMode: true,
        label: Localizer.localize('settings.audio_video.buffer_behind.label'),
        labelRenderer: renderTimeSliderLabel,
        setting: Setting.BufferBehind,
        valueRenderer: renderTimeSliderValue,
      })}
    </>
  );

  renderGreenStreamingSetting = (): React.Node => {
    const { isMaxBitrateAllowed, settings } = this.props;

    const greenStreaming = getBooleanSetting(settings, Setting.GreenStreaming);

    return (
      <RowSettingToggle checked={greenStreaming} className='settingsRow' disabled={!isMaxBitrateAllowed} onChange={this.handleBoolSettingOnChanged} setting={Setting.GreenStreaming}>
        <div className='settingsLabel column'>
          <div>
            {Localizer.localize('settings.audio_video.green_streaming.label')}
            <PictoLeaf className='leaf' />
          </div>
          <div className='hint'>{Localizer.localize('settings.audio_video.green_streaming.hint')}</div>
        </div>
      </RowSettingToggle>
    );
  };

  renderAudioVideoSettings = (): React.Node => {
    const { audioDataIndex, audioVideoRef, onWaypointChange } = this.props;

    return (
      <div className='settingsSection' data-index={audioDataIndex} ref={audioVideoRef}>
        {onWaypointChange && typeof audioDataIndex === 'number' ? <IntersectionWaypoint onChange={onWaypointChange(audioDataIndex)} /> : null}
        <div className='sectionTitle'>{Localizer.localize('settings.audio_video.title')}</div>
        {this.renderVolumeSetting()}
        {this.renderAudioTrackSetting()}
        {this.renderSubtitlesTrackSetting()}
        {this.renderAutoResumeSetting()}
        {this.renderExpertSettings()}
        {this.renderGreenStreamingSetting()}
      </div>
    );
  };

  render(): React.Node {
    const { isExpertMode } = this.state;

    return (
      <div className={clsx('sharedSettingsWrapper', isExpertMode && 'expert')}>
        {this.renderContentSettings()}
        {this.renderAudioVideoSettings()}
        <div className='settingsSection'>
          <RowSettingToggle checked={isExpertMode} className='settingsRow' onChange={this.handleExpertModeOnClick}>
            <div className='settingsLabel'>{Localizer.localize(isExpertMode ? 'profiles.profile_editor.expert_mode.disable' : 'profiles.profile_editor.expert_mode.enable')}</div>
          </RowSettingToggle>
          <RowSettingAction className='settingsRow' onClick={this.handleResetOnClick}>
            <div className='settingsLabel'>{Localizer.localize('settings.more.reset_settings')}</div>
          </RowSettingAction>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxSharedSettingsReducerStateType => {
  return {
    isMaxBitrateAllowed: state.appConfiguration.isMaxBitrateAllowed,
    settings: state.ui.settings,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxSharedSettingsDispatchToPropsType => {
  return {
    localClearWishlist: (): Promise<void> => dispatch(clearWishlist()),

    localDeleteFavoriteList: (): Promise<void> => dispatch(deleteFavoriteList()),

    localDeletePlayingHistory: (): Promise<void> => dispatch(deleteWholePlayingHistory()),

    localDeleteSearchHistory: () => dispatch(deleteSearchHistory()),

    localDeleteViewingHistory: (): Promise<void> => dispatch(deleteWholeViewingHistory()),

    localResetSettings: (): Promise<void> => dispatch(resetSettings()),

    localShowConfirmation: (data: CONFIRMATION_DATA_MODAL_TYPE, ariaLabel: string): void => dispatch(showConfirmationModal(data, ariaLabel)),

    localUpdateSetting: (setting?: Setting, value: SettingValue): void => dispatch(updateSetting(setting, value)),
  };
};

const SharedSettingsScreen: React.ComponentType<SharedSettingsPropType> = connect(mapStateToProps, mapDispatchToProps)(SharedSettings);

export default SharedSettingsScreen;
